/**
 * fileToResizedDataUrl — read an image File, downscale it to fit within
 * `maxDim` px on its longest side, and return a compressed JPEG base64 data
 * URL. Keeps uploads small and comfortably within the backend's ~2 MB cap
 * (important for large phone photos) while staying crisp enough for a tag.
 */
export async function fileToResizedDataUrl(file, { maxDim = 1024, quality = 0.85, mime = 'image/jpeg' } = {}) {
  if (!file || !file.type || !file.type.startsWith('image/')) {
    throw new Error('Please choose an image file');
  }
  const srcDataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Could not read the file'));
    r.readAsDataURL(file);
  });

  const image = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error('Could not load the image'));
    im.src = srcDataUrl;
  });

  const longest = Math.max(image.width, image.height) || 1;
  const scale = Math.min(1, maxDim / longest);
  const w = Math.max(1, Math.round(image.width * scale));
  const h = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  // White backdrop so transparent PNGs don't come out black as JPEG.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(image, 0, 0, w, h);

  return canvas.toDataURL(mime, quality);
}

/**
 * Approximate decoded byte size of a base64 `data:` URL.
 */
export function dataUrlBytes(dataUrl) {
  if (typeof dataUrl !== 'string') return 0;
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return 0;
  const b64 = dataUrl.slice(comma + 1);
  if (!b64) return 0;
  const padding = (b64.endsWith('==') ? 2 : (b64.endsWith('=') ? 1 : 0));
  return Math.max(0, Math.floor((b64.length * 3) / 4) - padding);
}

/**
 * fileToDataUrlUnderCap — read an image File and produce a compressed JPEG
 * data URL guaranteed to fit within `capBytes`. Progressively lowers JPEG
 * quality and, if needed, the max dimension until the encoded size is under
 * the cap. Throws if the image can't be brought under the cap (extremely rare
 * for photos; happens only for pathological inputs with a tiny cap).
 */
export async function fileToDataUrlUnderCap(file, capBytes, { mime = 'image/jpeg' } = {}) {
  if (!file || !file.type || !file.type.startsWith('image/')) {
    throw new Error('Please choose an image file');
  }
  if (!capBytes || capBytes <= 0) capBytes = 500 * 1024;

  const srcDataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Could not read the file'));
    r.readAsDataURL(file);
  });
  const image = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error('Could not load the image'));
    im.src = srcDataUrl;
  });

  const encodeAt = (maxDim, quality) => {
    const longest = Math.max(image.width, image.height) || 1;
    const scale = Math.min(1, maxDim / longest);
    const w = Math.max(1, Math.round(image.width * scale));
    const h = Math.max(1, Math.round(image.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(image, 0, 0, w, h);
    return canvas.toDataURL(mime, quality);
  };

  const dims = [1400, 1200, 1024, 860, 720, 560, 420];
  const qualities = [0.85, 0.75, 0.65, 0.55, 0.45, 0.35];
  let best = null;
  for (const dim of dims) {
    for (const q of qualities) {
      const url = encodeAt(dim, q);
      const bytes = dataUrlBytes(url);
      if (bytes <= capBytes) return url;
      best = url; // keep the smallest attempt as a fallback reference
    }
  }
  // Could not fit under the cap even at the most aggressive setting.
  throw new Error('IMAGE_TOO_LARGE');
}
